import { useEffect, useRef } from 'react'

/**
 * Polls while something is still happening, slowing down as it drags on and
 * pausing entirely while the tab is hidden.
 *
 * A fixed interval is wrong for a job that can take minutes: it is either too
 * slow to feel live when the user has just started something, or far too
 * chatty an hour later on a tab nobody is looking at. A 5s poll left open for
 * an hour is 720 requests; this is 73, and none at all while the tab is in
 * the background.
 *
 * Nothing should depend on the poll. It exists to keep a page that is being
 * watched roughly current -- the notification and the email are what actually
 * tell someone their job finished.
 *
 * @import import { useBackoffPoll } from '@/utils/hooks/useBackoffPoll'
 * @param {function} onTick Called on each poll; always the latest render's copy
 * @param {object} options
 * @param {boolean} options.enabled Poll only while true; stops and resets when false
 * @param {Array<{every: number, until: number}>} options.schedule Interval in ms,
 *   and the elapsed ms it applies up to. The last entry's `every` applies thereafter.
 */
export const useBackoffPoll = (onTick, { enabled, schedule }) => {
  // Kept in a ref so a new callback each render does not restart the clock,
  // which would hold the poll at its fastest step forever.
  const onTickRef = useRef(onTick)
  onTickRef.current = onTick

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    const startedAt = Date.now()
    let timer

    const intervalFor = (elapsed) =>
      (schedule.find((step) => elapsed < step.until) ?? schedule[schedule.length - 1]).every

    const queue = () => {
      timer = setTimeout(
        () => {
          // Hidden tabs are the case this exists for: browsers throttle their
          // timers but do not stop them, so without this a backgrounded page
          // keeps asking all afternoon.
          if (document.visibilityState === 'visible') {
            onTickRef.current()
          }
          queue()
        },
        intervalFor(Date.now() - startedAt)
      )
    }

    // Coming back to the tab asks once straight away rather than waiting out
    // whatever step the backoff had reached while nobody was looking.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        onTickRef.current()
      }
    }

    queue()
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
    // `schedule` is a module constant at both call sites, so it is stable.
  }, [enabled, schedule])
}
